import {
  DelayStopPopup,
  DriverAdditionalStopPopup,
  CompletedStopPopup,
  DeliveryReturnStopPopup,
  TrailerDropHookStopPopup,
  CurrentStopPopup,
  PendingStopOnTimePopup,
  PendingStopDelayedPopup,
} from "./Popups";
import reportConstants from "../constants";
import { v4 as uuidv4 } from "uuid";
import { getTimeStringFromDateTime, isStopLinkPresent } from "../util";
// initial helper function to render popups based on stop/task stype

const renderStopPopup = (task) => {
  const { REPORT_STOP_TYPES } = reportConstants;
  switch (task.type) {
    case REPORT_STOP_TYPES.delay:
      return <DelayStopPopup task={task} />;
    case REPORT_STOP_TYPES.clockInStop:
      return <DriverAdditionalStopPopup task={task} />;
    case REPORT_STOP_TYPES.gateOutStop:
      return <DriverAdditionalStopPopup task={task} />;
    case REPORT_STOP_TYPES.gateInStop:
      return <DriverAdditionalStopPopup task={task} />;
    case REPORT_STOP_TYPES.clockOutStop:
      return <DriverAdditionalStopPopup task={task} />;
    case REPORT_STOP_TYPES.dispatchTimeStop:
      return <DriverAdditionalStopPopup task={task} />;
    case REPORT_STOP_TYPES.delivery:
      return renderPickupDelStopPopup(task);
    case REPORT_STOP_TYPES.pickup:
      return renderPickupDelStopPopup(task);
    case REPORT_STOP_TYPES.delayedStop:
      return <PendingStopDelayedPopup task={task} />;
    case REPORT_STOP_TYPES.deliveryReturn:
      return <DeliveryReturnStopPopup task={task} />;
    case REPORT_STOP_TYPES.trailerHook:
      return <TrailerDropHookStopPopup task={task} />;
    case REPORT_STOP_TYPES.trailerDrop:
      return <TrailerDropHookStopPopup task={task} />;
    default:
      return;
  }
};

const renderPickupDelStopPopup = (task) => {
  if (task.isCurrentStop) {
    return <CurrentStopPopup task={task} />;
  } else if (task.data.isStopCompleted) {
    return <CompletedStopPopup task={task} />;
  } else {
    return <PendingStopOnTimePopup task={task} />;
  }
};

const isValidLink = (currentStopArrivalTime, nextStopArrivalTime) => {
  // this helper method can be used as a check to populate link for a stop/task on the chart.
  return new Date(nextStopArrivalTime) > new Date(currentStopArrivalTime);
};

const createClockInStop = ({
  driverData,
  routeData,
  stopType,
  stopTypeLabel,
}) => {
  return {
    id: uuidv4(),
    text: "",
    start_date: driverData.driverClockedInTime,
    end_date: driverData.driverClockedInTime,
    parent: routeData.routeId,
    type: stopType,
    arrivalTime: getTimeStringFromDateTime(driverData.driverClockedInTime),
    isLinkPresent: false,
    stopTypeLabel,
  };
};

const createClockOutStop = ({
  driverData,
  routeData,
  stopType,
  stopTypeLabel,
}) => {
  return {
    id: uuidv4(),
    text: "",
    start_date: driverData.driverClockedOutTime,
    end_date: driverData.driverClockedOutTime,
    parent: routeData.routeId,
    type: stopType,
    arrivalTime: getTimeStringFromDateTime(driverData.driverClockedOutTime),
    isLinkPresent: false,
    stopTypeLabel,
  };
};

const createGateOutStop = ({
  sortedStops,
  routeData,
  stopType,
  stopTypeLabel,
}) => {
  const gateOutStopId = uuidv4();
  return {
    id: gateOutStopId,
    text: "",
    start_date: routeData.gateOutTime,
    end_date: routeData.gateOutTime,
    parent: routeData.routeId,
    type: stopType,
    arrivalTime: getTimeStringFromDateTime(routeData.gateOutTime),
    isLinkPresent:
      isStopLinkPresent(sortedStops[0]) &&
      isValidLink(routeData.gateOutTime, sortedStops[0].actualArrivalTime),
    sourceStop: gateOutStopId,
    targetStop: sortedStops.length && sortedStops[0].stopId,
    stopTypeLabel,
  };
};

const createGateInStop = ({ routeData, stopType, stopTypeLabel }) => {
  const gateInStopId = uuidv4();
  return {
    id: gateInStopId,
    text: "",
    start_date: routeData.gateInTime,
    end_date: routeData.gateInTime,
    parent: routeData.routeId,
    type: stopType,
    arrivalTime: getTimeStringFromDateTime(routeData.gateInTime),
    isLinkPresent: false,
    stopTypeLabel,
  };
};

const createDispatchTimeStop = ({ routeData, stopType, stopTypeLabel }) => {
  const dispatchTimeStopId = uuidv4();
  return {
    id: dispatchTimeStopId,
    text: "",
    start_date: routeData.driverDispatchTime,
    end_date: routeData.driverDispatchTime,
    parent: routeData.routeId,
    type: stopType,
    arrivalTime: getTimeStringFromDateTime(routeData.driverDispatchTime),
    isLinkPresent: false,
    stopTypeLabel,
  };
};

const createGanttStop = (createStopData) => {
  const { REPORT_STOP_TYPES, REPORT_STOP_TYPE_LABEL } = reportConstants;
  switch (createStopData.stopType) {
    case REPORT_STOP_TYPES.clockInStop:
      return createClockInStop({
        ...createStopData,
        stopTypeLabel: REPORT_STOP_TYPE_LABEL.clockInStop,
      });
    case REPORT_STOP_TYPES.clockOutStop:
      return createClockOutStop({
        ...createStopData,
        stopTypeLabel: REPORT_STOP_TYPE_LABEL.clockOutStop,
      });
    case REPORT_STOP_TYPES.gateOutStop:
      return createGateOutStop({
        ...createStopData,
        stopTypeLabel: REPORT_STOP_TYPE_LABEL.gateOutStop,
      });
    case REPORT_STOP_TYPES.gateInStop:
      return createGateInStop({
        ...createStopData,
        stopTypeLabel: REPORT_STOP_TYPE_LABEL.gateInStop,
      });
    case REPORT_STOP_TYPES.dispatchTimeStop:
      return createDispatchTimeStop({
        ...createStopData,
        stopTypeLabel: REPORT_STOP_TYPE_LABEL.dispatchTimeStop,
      });
    default:
      return;
  }
};

export { renderStopPopup, createGanttStop };
